'use client';

import { Box } from '@chakra-ui/react';
import {
  autoUpdate,
  flip,
  type Placement,
  shift,
  useFloating,
} from '@floating-ui/react';
import { type ReactFlowState, useReactFlow, useStore } from '@xyflow/react';
import { memo, useCallback, useEffect, useMemo, type ReactNode } from 'react';

import { NODE_WIDTH_PX } from './constants';

interface VirtualAnchor {
  getBoundingClientRect: () => DOMRect;
}

function rectAt(x: number, y: number): DOMRect {
  return {
    x,
    y,
    top: y,
    left: x,
    bottom: y,
    right: x,
    width: 0,
    height: 0,
    toJSON: () => ({}),
  };
}

type NodeRect = { x: number; y: number; width: number };

interface AnchoredPopoverProps {
  nodeId?: string;
  flowPosition?: { x: number; y: number };
  containerRef: React.RefObject<HTMLDivElement | null>;
  placement?: Placement;
  children: ReactNode;
}

const AnchoredPopover = ({
  nodeId,
  flowPosition,
  containerRef,
  placement = 'right-start',
  children,
}: AnchoredPopoverProps) => {
  const { flowToScreenPosition } = useReactFlow();

  // Subscribe to just the anchored node's rect (custom equality), so the anchor
  // recomputes when that node is added/measured/moved — but NOT on every change
  // to other nodes, which would re-render the popover on any drag.
  const selectNodeRect = useCallback(
    (state: ReactFlowState): NodeRect | null => {
      if (nodeId == null) {
        return null;
      }
      const node = state.nodeLookup.get(nodeId);
      if (node == null) {
        return null;
      }
      return {
        x: node.internals.positionAbsolute.x,
        y: node.internals.positionAbsolute.y,
        width: node.measured?.width ?? NODE_WIDTH_PX,
      };
    },
    [nodeId],
  );
  const nodeRect = useStore(
    selectNodeRect,
    (a, b) => a?.x === b?.x && a?.y === b?.y && a?.width === b?.width,
  );

  // getBoundingClientRect resolves coordinates lazily (floating-ui calls it
  // outside render), so the container ref is never read during render and the
  // anchor always reflects the latest pan/zoom transform.
  const virtualAnchor = useMemo<VirtualAnchor>(
    () => ({
      getBoundingClientRect: () => {
        if (nodeRect != null) {
          const screen = flowToScreenPosition({
            x: nodeRect.x + nodeRect.width,
            y: nodeRect.y,
          });
          return rectAt(screen.x, screen.y);
        }
        if (flowPosition != null) {
          const screen = flowToScreenPosition(flowPosition);
          return rectAt(screen.x, screen.y);
        }
        const bounds = containerRef.current?.getBoundingClientRect();
        if (bounds != null) {
          return rectAt(bounds.left + bounds.width / 2, bounds.top + bounds.height / 2);
        }
        return rectAt(0, 0);
      },
    }),
    [nodeRect, flowPosition, flowToScreenPosition, containerRef],
  );

  const { refs, floatingStyles, update } = useFloating({
    placement,
    middleware: [
      flip({ padding: 16, fallbackPlacements: ['left-start', 'right-end', 'left-end', 'bottom-start', 'top-start'] }),
      shift({ padding: 16 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    refs.setPositionReference(virtualAnchor);
    update();
  }, [virtualAnchor, refs, update]);

  // Wrap the floating-ui setter in a stable callback ref so it isn't read during render.
  const setFloatingRef = useCallback(
    (el: HTMLDivElement | null) => refs.setFloating(el),
    [refs],
  );

  return (
    <Box ref={setFloatingRef} style={floatingStyles} zIndex={20}>
      {children}
    </Box>
  );
};

export default memo(AnchoredPopover);
